import React, { useState, useMemo, useCallback } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import moment from 'moment';
import AttemptsHistoryModal from './AttemptsHistoryModal';
import { getPathList } from '../hooks/useUserMarksSearch';

const UserMarkRow = React.memo(({
    lesson,
    index,
    user,
    indices,
    groupLesson = true,
    onOpenSummary,
    t
}) => {
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // 1. Attempts list computation
    const attempts = useMemo(() => {
        if (Array.isArray(lesson.Attempts) && lesson.Attempts.length > 0) {
            return lesson.Attempts;
        }
        return [{
            Id: lesson.Id,
            Mark: lesson.Mark,
            TotalMark: lesson.TotalMark,
            UserTime: lesson.UserTime,
            CreationDate: lesson.CreationDate
        }];
    }, [lesson]);

    // 2. Primary / Best Score Calculation
    const scoreStats = useMemo(() => {
        let bestMark = 0;
        let bestTotal = Number(lesson.TotalMark || 100);
        let bestPct = 0;
        let hasAnyResult = false;

        attempts.forEach(att => {
            if (att.Mark !== null && att.Mark !== undefined) {
                hasAnyResult = true;
                const max = Number(att.TotalMark || lesson.TotalMark || 0);
                const mark = Number(att.Mark);
                const pct = max > 0 ? Math.round((mark / max) * 100) : (mark > 0 ? Math.round(mark) : 0);

                if (pct >= bestPct) {
                    bestPct = pct;
                    bestMark = mark;
                    bestTotal = max;
                }
            }
        });

        // Primary attempt (most recent)
        const latestAtt = attempts[0] || {};
        const latestMax = Number(latestAtt.TotalMark || lesson.TotalMark || 100);
        const hasLatestResult = latestAtt.Mark !== null && latestAtt.Mark !== undefined;
        const latestMark = hasLatestResult ? Number(latestAtt.Mark) : 0;
        const latestPct = (hasLatestResult && latestMax > 0) ? Math.round((latestMark / latestMax) * 100) : 0;

        let statusClass = 'danger';
        let statusLabel = t('Pages.Lessons.ScoreNeedsImprovement') || 'بحاجة لتحسين';
        let statusColor = '#ef4444';
        let statusBg = '#fee2e2';

        if (!hasAnyResult) {
            statusClass = 'secondary';
            statusLabel = t('Pages.Lessons.NotSolvedYet') || 'لم يتم الحل بعد';
            statusColor = '#64748b';
            statusBg = '#f1f5f9';
        } else if (bestPct >= 90) {
            statusClass = 'success';
            statusLabel = `${t('Pages.Lessons.ScoreExcellent') || 'ممتاز'} 🌟`;
            statusColor = '#16a34a';
            statusBg = '#dcfce7';
        } else if (bestPct >= 75) {
            statusClass = 'primary';
            statusLabel = `${t('Pages.Lessons.ScoreVeryGood') || 'جيد جداً'} 👍`;
            statusColor = '#4f46e5';
            statusBg = '#eef2ff';
        } else if (bestPct >= 60) {
            statusClass = 'warning';
            statusLabel = `${t('Pages.Lessons.ScoreGood') || 'متوسط'} ⚡`;
            statusColor = '#f59e0b';
            statusBg = '#fef3c7';
        }

        const hasAnyLessonSolve = attempts.some(a => Boolean(a.HasLessonSolve)) || Boolean(lesson.HasLessonSolve);

        return {
            hasAnyResult,
            hasAnyLessonSolve,
            bestMark,
            bestTotal,
            bestPct,
            latestMark,
            latestTotal: latestMax,
            latestPct,
            statusClass,
            statusLabel,
            statusColor,
            statusBg
        };
    }, [attempts, lesson, t]);

    // 3. Breadcrumb Path list
    const pathList = useMemo(() => {
        const indexId = lesson.IndexId || lesson.Index?.Id;
        return getPathList(indices?.nodes, indexId);
    }, [indices, lesson]);

    // 4. Skills Parsing for Popover
    const displaySkills = useMemo(() => lesson.Skills || [], [lesson.Skills]);
    const uniqueTextTypes = useMemo(() => Array.from(new Set(displaySkills.map(s => s.TextTypeName).filter(Boolean))), [displaySkills]);

    const domainTree = useMemo(() => {
        const domainMap = {};
        displaySkills.forEach(s => {
            if (!s.SkillName) return;
            const domainName = s.DimensionName || s.DomainName || "المجال العام";
            const compName = s.DimensionComponentName || s.SubDomainName || "المحور الأساسي";

            if (!domainMap[domainName]) domainMap[domainName] = {};
            if (!domainMap[domainName][compName]) domainMap[domainName][compName] = new Set();
            domainMap[domainName][compName].add(s.SkillName);
        });

        return Object.keys(domainMap).map(dName => ({
            name: dName,
            components: Object.keys(domainMap[dName]).map(cName => ({
                name: cName,
                skills: Array.from(domainMap[dName][cName])
            }))
        }));
    }, [displaySkills]);

    // 5. Open Lesson Handler
    const handleOpenLesson = useCallback(() => {
        if (lesson.isGame) {
            window.openGameView ? window.openGameView(lesson.Id) : window.open(`/GameView/${lesson.Id}`, '_blank');
        } else {
            window.openLessonView ? window.openLessonView(lesson.Id) : window.open(`/LessonView/${lesson.Id}`, '_blank');
        }
    }, [lesson]);

    // 6. Handle Triggering Lesson Summary
    const handleTriggerSummary = useCallback((att = null) => {
        const target = att || attempts.find(a => a.Mark !== null && a.Mark !== undefined) || attempts[0];
        if (onOpenSummary) {
            onOpenSummary(lesson, target);
        }
    }, [attempts, lesson, onOpenSummary]);

    // Latest attempt time display
    const latestAttempt = attempts[0] || {};
    const clock = latestAttempt.UserTime?.Clock || lesson.UserTime?.Clock;
    let durationDisplay = '-';
    if (clock) {
        const h = clock.hour ? `${clock.hour}:` : '';
        const m = clock.minutes >= 10 ? clock.minutes : `0${clock.minutes || 0}`;
        const s = clock.seconds >= 10 ? clock.seconds : `0${clock.seconds || 0}`;
        durationDisplay = `${h}${m}:${s}`;
    } else if (latestAttempt.UserTime?.duration) {
        durationDisplay = `${latestAttempt.UserTime.duration} ث`;
    }

    return (
        <>
            {/* Modal: Dedicated Attempts History Modal */}
            {showHistoryModal && (
                <AttemptsHistoryModal
                    show={showHistoryModal}
                    onClose={() => setShowHistoryModal(false)}
                    lesson={lesson}
                    attempts={attempts}
                    onOpenSummary={onOpenSummary}
                    t={t}
                />
            )}

            <tr className="border-bottom hover-bg-light transition-all" style={{ background: '#ffffff' }}>
                {/* 1. Index */}
                <td className="text-center font-weight-bold text-muted" style={{ width: '45px', verticalAlign: 'middle' }}>
                    {index}
                </td>

                {/* 2. Lesson Thumbnail & Title & Breadcrumbs */}
                <td style={{ minWidth: '260px', verticalAlign: 'middle' }}>
                    <div className="d-flex align-items-center">
                        {/* Thumbnail */}
                        <div
                            className="position-relative overflow-hidden ml-3 rounded shadow-sm flex-shrink-0 cursor-pointer"
                            style={{ width: '56px', height: '56px', backgroundColor: '#f1f5f9' }}
                            onClick={handleOpenLesson}
                            title="انقر لفتح واستعراض الدرس"
                        >
                            <LazyLoadImage
                                src={lesson.Icon || "/assets/images/logo/130p150.png"}
                                alt={lesson.Name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                            {lesson.isGame && (
                                <span className="position-absolute badge badge-warning p-1" style={{ bottom: '2px', right: '2px', fontSize: '9px' }}>
                                    <i className="fas fa-gamepad"></i>
                                </span>
                            )}
                        </div>

                        {/* Title & Info */}
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                                <h6
                                    className="font-weight-bold mb-0 text-dark hover-primary cursor-pointer"
                                    onClick={handleOpenLesson}
                                    style={{ fontSize: '0.94rem' }}
                                    title={lesson.Name}
                                >
                                    {lesson.Name || lesson.title}
                                </h6>

                                {lesson.isGame ? (
                                    <span className="badge badge-warning px-2 py-0" style={{ fontSize: '0.72rem', borderRadius: '6px', width: 'auto' }}>
                                        لعبة تعليمية
                                    </span>
                                ) : lesson.isExam ? (
                                    <span className="badge badge-danger px-2 py-0" style={{ fontSize: '0.72rem', borderRadius: '6px', width: 'auto' }}>
                                        امتحان
                                    </span>
                                ) : (
                                    <span className="badge badge-primary px-2 py-0" style={{ fontSize: '0.72rem', borderRadius: '6px', backgroundColor: '#e0e7ff', color: '#4338ca', width: 'auto' }}>
                                        {t('Pages.Lessons.InteractiveLesson') || 'درس تفاعلي'}
                                    </span>
                                )}
                            </div>

                            {/* Breadcrumbs */}
                            {pathList && pathList.length > 0 && (
                                <div className="small text-muted mt-1 d-flex align-items-center flex-wrap" style={{ fontSize: '0.78rem', gap: '4px' }}>
                                    {pathList.map((node, pIdx) => (
                                        <span key={node.id} className="d-inline-flex align-items-center">
                                            <span>{node.label || node.Name}</span>
                                            {pIdx < pathList.length - 1 && (
                                                <i className="fal fa-angle-left mx-1 text-muted" style={{ fontSize: '0.65rem' }}></i>
                                            )}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </td>

                {/* 3. Score & Progress Bar */}
                <td style={{ minWidth: '170px', verticalAlign: 'middle' }}>
                    <div className="d-flex flex-column">
                        <div className="d-flex justify-content-between align-items-center small font-weight-bold mb-1">
                            {scoreStats.hasAnyResult ? (
                                <>
                                    <span className="text-dark font-weight-bold">
                                        {scoreStats.bestMark} / {scoreStats.bestTotal} {t('Pages.Lessons.Mark') || 'علامة'}
                                    </span>
                                    <span className="font-weight-bold" style={{ color: scoreStats.statusColor }}>
                                        %{scoreStats.bestPct}
                                    </span>
                                </>
                            ) : (
                                <span className="text-muted small">{t('Pages.Lessons.NoScoreYet') || 'لم تُسجل نتيجة بعد'}</span>
                            )}
                        </div>
                        <div className="lr-kpi-bar" style={{ height: '7px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                            <div
                                className="lr-kpi-bar-fill"
                                style={{
                                    width: `${scoreStats.hasAnyResult ? scoreStats.bestPct : 0}%`,
                                    background: scoreStats.statusColor,
                                    height: '100%'
                                }}
                            />
                        </div>
                        <div className="mt-1 d-flex align-items-center justify-content-between">
                            <span
                                className="status-chip py-0 px-2 small font-weight-bold"
                                style={{
                                    backgroundColor: scoreStats.statusBg,
                                    color: scoreStats.statusColor,
                                    borderRadius: '6px',
                                    fontSize: '0.72rem',
                                    width: 'auto'
                                }}
                            >
                                {scoreStats.statusLabel}
                            </span>
                            {groupLesson && scoreStats.hasAnyResult && attempts.length > 1 && (
                                <span className="small text-muted" style={{ fontSize: '0.72rem' }}>
                                    {t('Pages.Lessons.LatestAttempt') || 'آخر محاولة'}: %{scoreStats.latestPct}
                                </span>
                            )}
                        </div>
                    </div>
                </td>

                {/* 4. Attempts & Time */}
                <td style={{ minWidth: '150px', verticalAlign: 'middle' }}>
                    <div className="d-flex flex-column">
                        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                            {/* Clickable Badge to Open History Modal */}
                            <button
                                type="button"
                                className="btn btn-light btn-sm border px-2 py-1 font-weight-bold text-dark d-inline-flex align-items-center shadow-xs"
                                style={{ borderRadius: '6px', fontSize: '0.78rem', width: 'auto' }}
                                onClick={() => setShowHistoryModal(true)}
                                title={t('Pages.Lessons.ClickToViewAttemptsHistory') || 'انقر لعرض سِجلّ كافة المحاولات السابقة'}
                            >
                                <i className="fas fa-history ml-1 text-primary"></i>
                                <span>{attempts.length} {attempts.length === 1 ? (t('Pages.Lessons.AttemptSingle') || 'محاولة') : (t('Pages.Lessons.AttemptPlural') || 'محاولات')}</span>
                                {attempts.length > 1 && (
                                    <i className="fas fa-external-link-alt mr-1 text-muted" style={{ fontSize: '0.65rem' }}></i>
                                )}
                            </button>
                        </div>

                        {/* Date & Practice Time */}
                        <div className="small text-muted mt-1 d-flex align-items-center justify-content-between" style={{ fontSize: '0.76rem' }}>
                            <span>
                                <i className="far fa-calendar-alt ml-1 text-muted"></i>
                                {latestAttempt.CreationDate ? moment(latestAttempt.CreationDate).format('YYYY-MM-DD') : '-'}
                            </span>
                            <span>
                                <i className="far fa-clock ml-1 text-muted"></i>
                                {durationDisplay}
                            </span>
                        </div>
                    </div>
                </td>

                {/* 5. Action Buttons (Lesson Summary with Skills, Open Lesson with Eye Icon, Skills Tree) */}
                <td className="text-center" style={{ minWidth: '190px', verticalAlign: 'middle' }}>
                    <div className="d-flex align-items-center justify-content-center" style={{ gap: '8px' }}>
                        {/* 🌟 Lesson Summary with Skills Button (ONLY shown if lesson solves exist) */}
                        {scoreStats.hasAnyLessonSolve && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary shadow-sm px-3 py-1 font-weight-bold d-inline-flex align-items-center"
                                onClick={() => handleTriggerSummary()}
                                title={t('Pages.Lessons.ViewSummary') || 'عرض تفاصيل نتائج الدرس ومؤشرات المهارات'}
                                style={{
                                    borderRadius: '8px',
                                    fontSize: '0.80rem',
                                    backgroundColor: '#4f46e5',
                                    borderColor: '#4f46e5',
                                    width: 'auto'
                                }}
                            >
                                <i className="fad fa-chart-pie ml-1"></i>
                                <span>{t('Pages.Lessons.ViewSummary') || 'نتائج الدرس والمهارات'}</span>
                            </button>
                        )}

                        {/* 👁️ View / Open Lesson Button (Eye Icon instead of Play Icon) */}
                        <button
                            type="button"
                            className="btn btn-sm btn-light border shadow-sm p-1 d-inline-flex align-items-center justify-content-center"
                            onClick={handleOpenLesson}
                            title={t('Pages.Lessons.OpenLesson') || 'عرض وفتح الدرس'}
                            style={{ borderRadius: '8px', width: '34px', height: '34px', minWidth: '34px' }}
                        >
                            <i className="far fa-eye text-primary" style={{ fontSize: '1rem' }}></i>
                        </button>

                        {/* 🎯 Skills Tree Popover */}
                        {displaySkills.length > 0 && (
                            <OverlayTrigger
                                trigger="click"
                                rootClose
                                placement="top"
                                overlay={
                                    <Popover id={`popover-skills-${lesson.Id}`} style={{ direction: 'rtl', textAlign: 'right', maxWidth: '380px', zIndex: 9999 }}>
                                        <Popover.Title as="h3" className="font-weight-bold text-dark d-flex align-items-center">
                                            <i className="fad fa-award text-warning ml-2"></i>
                                            <span>{t('Pages.Lessons.SkillsStandards') || 'المهارات والمعايير التعليمية'}</span>
                                        </Popover.Title>
                                        <Popover.Content style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                            {uniqueTextTypes.length > 0 && (
                                                <div className="mb-2 pb-2 border-bottom">
                                                    <div className="small font-weight-bold text-info mb-1">
                                                        <i className="fad fa-file-alt ml-1"></i> {t('Pages.Lessons.TextTypesTitle') || 'أنواع النصوص:'}
                                                    </div>
                                                    {uniqueTextTypes.map((name, idx) => (
                                                        <span key={`tt-${idx}`} className="badge badge-light border ml-1 mb-1 p-1 small" style={{ width: 'auto' }}>
                                                            {name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}

                                            {domainTree.map((domain, dIdx) => (
                                                <div key={`dom-${dIdx}`} className="mb-2">
                                                    <div className="small font-weight-bold text-primary mb-1">
                                                        <i className="fas fa-folder text-warning ml-1"></i> {domain.name}
                                                    </div>
                                                    <div className="mr-3 pr-2" style={{ borderRight: '2px solid #e2e8f0' }}>
                                                        {domain.components.map((comp, cIdx) => (
                                                            <div key={`cmp-${cIdx}`} className="mb-1">
                                                                <div className="small text-muted font-weight-bold">
                                                                    <i className="fas fa-angle-left ml-1 text-info"></i> {comp.name}
                                                                </div>
                                                                <div className="mr-2">
                                                                    {comp.skills.map((sName, sIdx) => (
                                                                        <div key={`sk-${sIdx}`} className="small text-dark">
                                                                            <i className="fas fa-check-circle text-success ml-1" style={{ fontSize: '9px' }}></i>
                                                                            <span>{sName}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            ))}
                                        </Popover.Content>
                                    </Popover>
                                }
                            >
                                <button
                                    type="button"
                                    className="btn btn-sm btn-light border shadow-sm p-1 d-inline-flex align-items-center justify-content-center"
                                    title={t('Pages.Lessons.ViewSkillsTree') || 'استعراض شجرة المهارات والمعايير'}
                                    style={{ borderRadius: '8px', width: '34px', height: '34px', minWidth: '34px' }}
                                >
                                    <i className="fad fa-lightbulb text-warning" style={{ fontSize: '1rem' }}></i>
                                </button>
                            </OverlayTrigger>
                        )}
                    </div>
                </td>
            </tr>
        </>
    );
});

export default UserMarkRow;
